import { useEffect, useState } from 'react';
import Icon from '../../shared/components/Icons';
import Barcode from '../../shared/components/Barcode';
import { formatCharge, formatPrice } from '../../shared/data/common';
import { getFlightBooking } from '../../api/flights';
import { useAuth } from '../../auth/AuthContext';

const statusText = {
  SUCCESS: ['Confirmed', 'live'],
  ON_HOLD: ['On hold', ''],
  PENDING: ['Pending', ''],
  CANCELLED: ['Cancelled', 'cancelled'],
  FAILED: ['Failed', 'cancelled'],
  ABORTED: ['Failed', 'cancelled'],
  UNCONFIRMED: ['Released', 'cancelled'],
};

const travellerName = (t) => [t.title, t.firstName, t.lastName].filter(Boolean).join(' ');

/** Booking barcode per passenger and flight: PNR/ROUTE/LAST/FIRST. Not a boarding pass. */
const barcodeValue = (pnr, segmentKey, t) =>
  [pnr, segmentKey, t.lastName, t.firstName].map((part) => String(part || '').trim().toUpperCase().replace(/\s+/g, ' ')).join('/');

function ServiceCell({ service, empty }) {
  if (!service) return <span className="muted">{empty}</span>;
  return (
    <span className="fbs-service">
      <b>{service.description}</b>
      <span className="muted small">{service.amount ? formatPrice(service.amount) : 'Free'}</span>
    </span>
  );
}

/**
 * Live supplier booking: status, PNR, tickets and the seat, meal and baggage booked for each
 * traveller on each flight, read from the supplier's booking details.
 */
export default function FlightBookingServices({ booking }) {
  const { auth } = useAuth();
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ loading: true, details: null, error: '' });
  const supplierBookingId = booking?.supplierBookingId;
  const email = booking?.contact?.email;
  const token = auth?.accessToken;

  useEffect(() => {
    if (!supplierBookingId) return undefined;
    const controller = new AbortController();
    setState({ loading: true, details: null, error: '' });
    getFlightBooking(supplierBookingId, email, token, controller.signal)
      .then((details) => setState({ loading: false, details, error: '' }))
      .catch((error) => {
        if (error.name === 'AbortError') return;
        setState({
          loading: false,
          details: null,
          error: error.status === 404
            ? 'We could not find this booking with the airline system for these details.'
            : error.message || 'Unable to load the latest booking details.',
        });
      });
    return () => controller.abort();
  }, [supplierBookingId, email, token, attempt]);

  if (!supplierBookingId) return null;

  if (state.loading) {
    return (
      <section className="panel fbs" role="status">
        <h3><Icon name="bag" size={18} /> Seats, meals & baggage</h3>
        <p className="muted">Fetching your booking from the airline…</p>
      </section>
    );
  }

  if (state.error) {
    return (
      <section className="panel fbs">
        <h3><Icon name="bag" size={18} /> Seats, meals & baggage</h3>
        <p className="text-coral" role="alert">{state.error}</p>
        <button type="button" className="btn btn-outline" onClick={() => setAttempt(attempt + 1)}>Try again</button>
      </section>
    );
  }

  const { details } = state;
  const [statusLabel, statusClass] = statusText[details.status] || [details.status, ''];
  const pnrs = [...new Set(details.travellers.flatMap((t) => t.segments.map((s) => s.pnr)).filter(Boolean))];
  const extras = details.travellers.flatMap((t) => t.segments)
    .reduce((sum, s) => sum + (s.seat?.amount || 0) + (s.meal?.amount || 0) + (s.baggage?.amount || 0), 0);

  return (
    <section className="panel fbs">
      <div className="fbs-head">
        <h3><Icon name="bag" size={18} /> Seats, meals & baggage</h3>
        <span className={`status-pill ${statusClass}`}>{statusLabel}</span>
      </div>
      <div className="fbs-ids">
        <div><span className="muted small">Airline PNR</span><b>{pnrs.join(', ') || 'Awaiting airline'}</b></div>
        <div><span className="muted small">{details.supplierCode} booking ID</span><b>{details.supplierBookingId}</b></div>
        {details.amount != null && <div><span className="muted small">Amount charged</span><b>{formatCharge(details.amount)}</b></div>}
        {extras > 0 && <div><span className="muted small">Add-ons</span><b>{formatPrice(extras)}</b></div>}
      </div>

      {details.segments.map((segment) => (
        <div key={segment.key} className="fbs-segment">
          <h4>{segment.from} <Icon name="arrow" size={13} /> {segment.to} <span className="muted small">· {segment.flightNumber}</span></h4>
          <div className="fbs-table-wrap">
            <table className="data-table fbs-table">
              <thead><tr><th>Traveller</th><th>Seat</th><th>Meal</th><th>Extra baggage</th><th>Ticket</th></tr></thead>
              <tbody>
                {details.travellers.map((t, i) => {
                  const s = t.segments.find((x) => x.segmentKey === segment.key) || {};
                  return (
                    <tr key={i}>
                      <td className="fbs-traveller">
                        <b>{travellerName(t)}</b><span className="muted small fbs-pax">{t.paxType?.toLowerCase()}</span>
                        {s.pnr && (
                          <Barcode value={barcodeValue(s.pnr, segment.key, t)}
                            label={`Booking barcode for ${travellerName(t)}, PNR ${s.pnr}, ${segment.from} to ${segment.to}`} />
                        )}
                      </td>
                      <td data-label="Seat"><ServiceCell service={s.seat} empty="At check-in" /></td>
                      <td data-label="Meal"><ServiceCell service={s.meal} empty="—" /></td>
                      <td data-label="Extra baggage"><ServiceCell service={s.baggage} empty="Standard allowance" /></td>
                      <td data-label="Ticket">{s.ticketNumber || <span className="muted">{s.pnr ? 'Issuing' : '—'}</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
      <p className="muted small fbs-note">Barcodes identify your booking at the airline counter. They are not boarding passes — check in online or at the airport to get yours.</p>
    </section>
  );
}
